/**
 * ConversationList - Renders the list of DM, group, and club conversations
 * with a "New message" button. Pure UI: receives conversations + handlers.
 */

import React from 'react';
import UserAvatar from '../Profile/UserAvatar';

function ConversationList({ conversations, loading, onSelect, onNewMessage }) {
  if (loading) {
    return (
      <div className="p-4">
        <p className="text-silver text-sm">Loading...</p>
      </div>
    );
  }
  return (
    <>
      <div className="flex-shrink-0 p-4 border-b border-shadow">
        <button
          type="button"
          onClick={onNewMessage}
          className="w-full py-3 rounded-xl bg-ether text-void font-medium hover:opacity-90 transition-opacity"
        >
          New message
        </button>
      </div>
      {!conversations?.length ? (
        <div className="p-4">
          <p className="text-silver text-sm">Your first message is waiting. Search above or reach out from a profile.</p>
        </div>
      ) : (
        <ul className="divide-y divide-shadow">
          {conversations.map((c) => {
            const isClub = c.type === 'club';
            const isGroup = c.type === 'group';
            const displayName = isClub
              ? c.club_name
              : isGroup
                ? (c.display_name || 'Group')
                : (c.other_user?.displayname || c.other_user?.username || 'Unknown');
            const otherId = c.other_user?.user_id;
            const showYouPrefix = !isClub && !isGroup && c.last_message_sender_id !== otherId;
            const key = isClub ? `club-${c.club_id}` : isGroup ? `group-${c.group_conversation_id}` : c.conversation_id;
            const isUnread = c.is_unread === true;
            const avatarUser = isClub
              ? {
                  user_id: c.club_id,
                  username: c.club_name,
                  displayname: displayName,
                  profile_pic_url: c.club_avatar_url,
                }
              : isGroup
                ? {
                    user_id: c.group_conversation_id,
                    username: c.display_name,
                    displayname: displayName,
                    profile_pic_url: c.avatar_url,
                    default_profile_color: c.default_profile_color,
                  }
                : c.other_user;
            return (
              <li key={key}>
                <button
                  type="button"
                  onClick={() => onSelect(c)}
                  className={`w-full flex items-center gap-3 p-4 text-left transition-colors border-l-2 ${
                    isUnread
                      ? 'bg-ether/10 border-ether hover:bg-ether/15'
                      : 'border-transparent hover:bg-charcoal'
                  }`}
                >
                  <UserAvatar
                    user={avatarUser}
                    className={`w-12 h-12 flex-shrink-0 ${isClub ? 'rounded-xl' : 'rounded-full'}`}
                    emojiClassName="text-lg"
                    alt={displayName}
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`${isUnread ? 'font-bold text-ether' : 'font-medium text-ash'} truncate`}>
                      {displayName}
                    </p>
                    <p className={`${isUnread ? 'text-ash font-medium' : 'text-silver'} text-sm truncate`}>
                      {showYouPrefix ? 'You: ' : ''}
                      {c.last_message_preview || 'No messages yet'}
                    </p>
                  </div>
                  {isUnread && (
                    <span
                      className="h-2.5 w-2.5 rounded-full bg-ether flex-shrink-0"
                      aria-label="Unread conversation"
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

export default ConversationList;
